import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Logo, Wordmark } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Coffee, FileText, Shield, ScrollText, Users, LogOut, CheckCircle2, Clock, Archive, Send, Plus, Trash2, ArrowUp, ArrowDown,
} from "lucide-react";
import { generateSteps, POUR_OVER_MAX_SECONDS, timeLabel } from "@/lib/brew";

const BREW_METHODS = ["Pour Over", "Pour-Over Immersion", "French Press", "AeroPress", "Espresso", "Moka Pot", "South Indian Filter"];

export default function Admin() {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(() => {
    api.get("/admin/me").then(({ data }) => setAdmin(data.admin)).catch(() => setAdmin(null)).finally(() => setLoading(false));
  }, []);
  useEffect(loadMe, [loadMe]);

  if (loading) return <div className="min-h-screen bg-[#081F5C]" />;
  if (!admin) return <AdminLogin />;
  return <AdminDashboard admin={admin} onLogout={() => setAdmin(null)} />;
}

function AdminLogin() {
  const [email, setEmail] = useState("");
  const [devLink, setDevLink] = useState("");
  const [sent, setSent] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");

  const request = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/request-login", { email: email.trim() });
      setSent(true);
      if (data.dev_link) setDevLink(data.dev_link);
    } catch { toast.error("Something went wrong"); }
  };

  return (
    <div className="min-h-screen bg-[#081F5C] flex flex-col items-center justify-center px-6" data-testid="admin-login">
      <Logo size={72} />
      <h1 className="mt-5 text-2xl"><Wordmark className="text-[#EDE0CC]" /> <span className="font-brand">Admin</span></h1>
      <p className="mt-1 text-sm text-[#EDE0CC]/60">Wonderbean Coffee Co. staff only</p>
      {error && <p className="mt-3 text-sm text-[#E56B6B]">That sign-in link was invalid or expired.</p>}

      <form onSubmit={request} className="w-full max-w-sm mt-8 space-y-4">
        <Input type="email" placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input"
          className="h-12 rounded-xl bg-[#051540] border-[#EDE0CC]/30 text-[#EDE0CC] placeholder:text-[#EDE0CC]/40" />
        <Button type="submit" data-testid="admin-request-btn" className="w-full h-12 rounded-full bg-[#EDE0CC] text-[#081F5C] font-semibold hover:bg-[#E5D3B8] flex items-center justify-center gap-2">
          <Send size={16} /> Send sign-in link
        </Button>
      </form>

      {sent && (
        <div className="w-full max-w-sm mt-5 rounded-2xl border border-[#EDE0CC]/20 bg-[#0D2875] p-4 text-sm text-[#EDE0CC]/80" data-testid="admin-sent-note">
          If this email is an authorized admin, a sign-in link has been sent.
          {devLink && (
            <>
              <div className="mt-2 text-xs text-[#EDE0CC]/60">Development mode — use this link (email delivery via Resend to be configured):</div>
              <a href={devLink} data-testid="admin-dev-link" className="mt-1 block break-all underline text-[#EDE0CC]">{devLink}</a>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const SECTIONS = [
  { key: "recipes", label: "Guided Recipes", icon: Coffee },
  { key: "content", label: "Home & Content", icon: FileText },
  { key: "legal", label: "Legal Documents", icon: ScrollText },
  { key: "admins", label: "Admin Users", icon: Users },
  { key: "audit", label: "Audit Log", icon: Shield },
];

function AdminDashboard({ admin, onLogout }) {
  const [tab, setTab] = useState("recipes");

  const logout = async () => {
    await api.post("/admin/logout").catch(() => {});
    onLogout();
  };

  return (
    <div className="min-h-screen bg-[#081F5C] text-[#EDE0CC]" data-testid="admin-dashboard">
      <div className="sticky top-0 z-30 bg-[#081F5C]/90 backdrop-blur-md border-b border-[#EDE0CC]/15">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 h-14">
          <Logo size={34} />
          <div className="flex-1">
            <div className="text-sm font-semibold"><Wordmark /> Admin</div>
            <div className="text-xs text-[#EDE0CC]/60">{admin.email} · {admin.role === "super_admin" ? "Super Admin" : "Admin"}</div>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn" className="w-10 h-10 rounded-full flex items-center justify-center active:scale-95"><LogOut size={18} /></button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4">
        <div className="flex gap-2 overflow-x-auto no-scrollbar py-3">
          {SECTIONS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
              className={`flex items-center gap-1.5 h-9 px-3 rounded-full text-sm whitespace-nowrap ${tab === key ? "bg-[#EDE0CC] text-[#081F5C] font-semibold" : "border border-[#EDE0CC]/25 text-[#EDE0CC]"}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        <div className="pb-16 pt-2">
          {tab === "recipes" && <RecipesAdmin />}
          {tab === "content" && <ContentAdmin />}
          {tab === "legal" && <LegalAdmin admin={admin} />}
          {tab === "admins" && <AdminsAdmin admin={admin} />}
          {tab === "audit" && <AuditAdmin />}
        </div>
      </div>
    </div>
  );
}

function RecipesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/brewers").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const publish = async (id) => {
    try { await api.post(`/admin/brewers/${id}/publish`); toast.success("Published"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
  };
  const archive = async (id) => {
    try { await api.post(`/admin/brewers/${id}/archive`); toast.success("Archived"); load(); } catch { toast.error("Failed"); }
  };

  const save = async () => {
    const body = { ...editing };
    try {
      if (editing.id) await api.put(`/admin/brewers/${editing.id}`, body);
      else await api.post("/admin/brewers", body);
      toast.success("Saved as draft");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  if (editing) return <RecipeForm value={editing} setValue={setEditing} onSave={save} onCancel={() => setEditing(null)} />;

  return (
    <div className="space-y-3">
      <button onClick={() => setEditing({ name: "", brewer: "Pour Over", description: "", roast: "Medium", coffee: 15, water: 240, yield_g: null, ratio: 16, grind: "Medium-Fine", temperature: 93, time_label: "~3 min", difficulty: "Intermediate" })}
        data-testid="admin-new-recipe" className="w-full h-11 rounded-full border border-dashed border-[#EDE0CC]/40 text-[#EDE0CC] text-sm">
        + New guided recipe
      </button>
      {items.map((b) => (
        <div key={b.id} className="rounded-2xl border border-[#EDE0CC]/20 bg-[#0D2875] p-4" data-testid={`admin-recipe-${b.slug}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold">{b.name}</div>
              <div className="text-xs text-[#EDE0CC]/60">{b.brewer} · v{b.version} · {b.coffee}g / {b.yield_g ? b.yield_g + "g" : b.water + "ml"} · 1:{b.ratio}</div>
            </div>
            <StatusChip status={b.status} />
          </div>
          <div className="mt-3 flex gap-2 flex-wrap">
            <MiniBtn onClick={() => setEditing({ ...b })} label="Edit" testid={`admin-edit-${b.slug}`} icon={FileText} />
            <MiniBtn onClick={() => publish(b.id)} label="Publish" testid={`admin-publish-${b.slug}`} icon={CheckCircle2} primary />
            <MiniBtn onClick={() => archive(b.id)} label="Archive" testid={`admin-archive-${b.slug}`} icon={Archive} />
          </div>
        </div>
      ))}
    </div>
  );
}

function RecipeForm({ value, setValue, onSave, onCancel }) {
  const set = (patch) => setValue({ ...value, ...patch });
  const num = (v) => (v === "" ? "" : parseFloat(v));
  const esp = (value.brewer || "").toLowerCase() === "espresso";
  const auto = generateSteps(value.brewer, value.coffee || 0, esp ? (value.yield_g || 0) : (value.water || 0), value.yield_g);
  const steps = value.steps && value.steps.length ? value.steps : auto.steps;
  const total = steps.reduce((a, s) => a + (Number(s.seconds) || 0), 0);
  const pourWarn = (value.brewer || "").toLowerCase() === "pour over" && total > POUR_OVER_MAX_SECONDS;

  const updateStep = (i, patch) => set({ steps: value.steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) });
  const moveStep = (i, d) => { const j = i + d; if (j < 0 || j >= value.steps.length) return; const st = [...value.steps]; [st[i], st[j]] = [st[j], st[i]]; set({ steps: st }); };
  const removeStep = (i) => set({ steps: value.steps.filter((_, idx) => idx !== i) });
  const addStep = () => set({ steps: [...(value.steps || []), { label: "Step", instruction: "", seconds: 10 }] });

  const si = "h-9 rounded-lg bg-[#081F5C] border-[#EDE0CC]/30 text-[#EDE0CC] text-sm";

  return (
    <div className="space-y-3" data-testid="admin-recipe-form">
      {[["name", "Name"], ["brewer", "Brewer"], ["description", "Description"], ["roast", "Recommended roast"],
        ["grind", "Grind"], ["time_label", "Time label"], ["difficulty", "Difficulty"]].map(([k, label]) => (
        <Labeled key={k} label={label}>{k === "brewer" ? <select value={value.brewer || "Pour Over"} onChange={(e) => set({ brewer: e.target.value })} data-testid="admin-field-brewer" className={`${ai} w-full px-3`}>{BREW_METHODS.map((method) => <option key={method} value={method}>{method}</option>)}</select> : <Input value={value[k] ?? ""} onChange={(e) => set({ [k]: e.target.value })} data-testid={`admin-field-${k}`} className={ai} />}</Labeled>
      ))}
      <div className="grid grid-cols-2 gap-2">
        <Labeled label="Coffee (g)"><Input type="number" value={value.coffee} onChange={(e) => set({ coffee: num(e.target.value) })} data-testid="admin-field-coffee" className={ai} /></Labeled>
        <Labeled label="Water (ml)"><Input type="number" value={value.water} onChange={(e) => set({ water: num(e.target.value) })} data-testid="admin-field-water" className={ai} /></Labeled>
        <Labeled label="Yield (g, espresso)"><Input type="number" value={value.yield_g ?? ""} onChange={(e) => set({ yield_g: num(e.target.value) || null })} className={ai} /></Labeled>
        <Labeled label="Ratio"><Input type="number" value={value.ratio} onChange={(e) => set({ ratio: num(e.target.value) })} data-testid="admin-field-ratio" className={ai} /></Labeled>
        <Labeled label="Temperature (°C)"><Input type="number" value={value.temperature} onChange={(e) => set({ temperature: num(e.target.value) })} className={ai} /></Labeled>
      </div>

      <div className="rounded-2xl border border-[#EDE0CC]/20 bg-[#0D2875] p-3" data-testid="admin-steps-editor">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">Timed steps</div>
          <div className={`text-xs ${pourWarn ? "text-[#E56B6B]" : "text-[#EDE0CC]/70"}`} data-testid="admin-steps-total">Total {timeLabel(total)}</div>
        </div>
        {value.steps && value.steps.length ? (
          <div className="space-y-2">
            {value.steps.map((s, i) => (
              <div key={i} className="rounded-xl border border-[#EDE0CC]/15 bg-[#051540] p-2 space-y-1" data-testid={`admin-step-${i}`}>
                <div className="flex gap-1 items-center">
                  <Input value={s.label} onChange={(e) => updateStep(i, { label: e.target.value })} placeholder="Label" className={si} />
                  <Input type="number" value={s.seconds} onChange={(e) => updateStep(i, { seconds: parseInt(e.target.value || 0, 10) })} data-testid={`admin-step-seconds-${i}`} className={`${si} w-16`} />
                  <button type="button" onClick={() => moveStep(i, -1)} className="w-8 h-8 rounded-lg flex items-center justify-center text-[#EDE0CC]/70"><ArrowUp size={14} /></button>
                  <button type="button" onClick={() => moveStep(i, 1)} className="w-8 h-8 rounded-lg flex items-center justify-center text-[#EDE0CC]/70"><ArrowDown size={14} /></button>
                  <button type="button" onClick={() => removeStep(i)} data-testid={`admin-step-remove-${i}`} className="w-8 h-8 rounded-lg flex items-center justify-center text-[#E56B6B]"><Trash2 size={14} /></button>
                </div>
                <Input value={s.instruction} onChange={(e) => updateStep(i, { instruction: e.target.value })} placeholder="Instruction" className={si} />
              </div>
            ))}
            <div className="flex gap-2">
              <button type="button" onClick={addStep} data-testid="admin-add-step" className="flex items-center gap-1 h-9 px-3 rounded-full border border-[#EDE0CC]/30 text-xs"><Plus size={14} /> Add step</button>
              <button type="button" onClick={() => set({ steps: null })} className="h-9 px-3 rounded-full border border-[#EDE0CC]/30 text-xs">Reset to auto</button>
            </div>
          </div>
        ) : (
          <div>
            <div className="text-xs text-[#EDE0CC]/60 mb-2">Steps are auto-generated from the values above. Customize to fine-tune the timeline.</div>
            <button type="button" onClick={() => set({ steps: auto.steps.map((x) => ({ ...x })) })} data-testid="admin-customize-steps" className="flex items-center gap-1 h-9 px-3 rounded-full border border-[#EDE0CC]/30 text-xs"><Plus size={14} /> Customize timed steps</button>
          </div>
        )}
        {pourWarn && <p className="mt-2 text-xs text-[#E56B6B]" data-testid="admin-pourover-warn">Pour Over timeline exceeds 210s and cannot be published.</p>}
      </div>

      <div className="flex gap-2 pt-2">
        <Button onClick={onSave} disabled={pourWarn} data-testid="admin-save-recipe" className="flex-1 h-12 rounded-full bg-[#EDE0CC] text-[#081F5C] font-semibold hover:bg-[#E5D3B8] disabled:opacity-40">Save draft</Button>
        <Button onClick={onCancel} variant="outline" className="h-12 rounded-full border-[#EDE0CC]/40 text-[#EDE0CC] hover:bg-[#EDE0CC]/10">Cancel</Button>
      </div>
      <p className="text-xs text-[#EDE0CC]/50">Pour Over recipes exceeding 210 seconds cannot be published.</p>
    </div>
  );
}

function ContentAdmin() {
  const [content, setContent] = useState([]);
  const load = () => api.get("/admin/content").then(({ data }) => setContent(data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async (key, value) => {
    try { await api.put("/admin/content", { key, value }); toast.success("Content updated"); load(); }
    catch { toast.error("Save failed"); }
  };

  return (
    <div className="space-y-4">
      {content.map((c) => (
        <div key={c.key} className="rounded-2xl border border-[#EDE0CC]/20 bg-[#0D2875] p-4" data-testid={`admin-content-${c.key}`}>
          <div className="text-sm font-semibold uppercase tracking-wide mb-2">{c.key}</div>
          {Object.keys(c.value).map((field) => (
            <Labeled key={field} label={field}>
              <textarea defaultValue={c.value[field]} rows={c.value[field]?.length > 80 ? 4 : 2}
                onChange={(e) => (c.value[field] = e.target.value)} data-testid={`admin-content-${c.key}-${field}`}
                className="w-full rounded-xl bg-[#051540] border border-[#EDE0CC]/30 text-[#EDE0CC] p-2 text-sm" />
            </Labeled>
          ))}
          <Button onClick={() => save(c.key, c.value)} data-testid={`admin-save-content-${c.key}`} className="mt-2 h-10 rounded-full bg-[#EDE0CC] text-[#081F5C] font-semibold hover:bg-[#E5D3B8]">Save</Button>
        </div>
      ))}
    </div>
  );
}

function LegalAdmin({ admin }) {
  const [docs, setDocs] = useState([]);
  const [draft, setDraft] = useState({ doc_type: "terms", body: "", effective_date: "", change_summary: "" });
  const load = () => api.get("/admin/legal").then(({ data }) => setDocs(data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const isSuper = admin.role === "super_admin";

  const publish = async () => {
    try { await api.post("/admin/legal/publish", draft); toast.success("Published new version"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
  };

  return (
    <div className="space-y-4">
      {!isSuper && <p className="text-sm text-[#E56B6B]">Only a Super Admin may edit legal documents.</p>}
      <div className="space-y-2">
        {docs.filter((d) => d.status === "published").map((d) => (
          <div key={d.id} className="rounded-2xl border border-[#EDE0CC]/20 bg-[#0D2875] p-4" data-testid={`admin-legal-${d.doc_type}`}>
            <div className="flex items-center justify-between">
              <div className="font-semibold capitalize">{d.doc_type}</div>
              <span className="text-xs text-[#EDE0CC]/60">v{d.version} · {d.effective_date}</span>
            </div>
          </div>
        ))}
      </div>
      {isSuper && (
        <div className="rounded-2xl border border-[#EDE0CC]/20 bg-[#0D2875] p-4 space-y-3">
          <div className="text-sm font-semibold">Publish new version</div>
          <div className="flex gap-2">
            {["terms", "privacy"].map((t) => (
              <button key={t} onClick={() => setDraft({ ...draft, doc_type: t })} data-testid={`admin-legal-type-${t}`}
                className={`flex-1 h-10 rounded-full text-sm capitalize ${draft.doc_type === t ? "bg-[#EDE0CC] text-[#081F5C] font-semibold" : "border border-[#EDE0CC]/30"}`}>{t}</button>
            ))}
          </div>
          <Input placeholder="Effective date (e.g. 4 September 2026)" value={draft.effective_date} onChange={(e) => setDraft({ ...draft, effective_date: e.target.value })} className={ai} />
          <Input placeholder="Change summary" value={draft.change_summary} onChange={(e) => setDraft({ ...draft, change_summary: e.target.value })} className={ai} />
          <textarea placeholder="Document body" rows={6} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="admin-legal-body"
            className="w-full rounded-xl bg-[#051540] border border-[#EDE0CC]/30 text-[#EDE0CC] p-2 text-sm" />
          <Button onClick={publish} data-testid="admin-legal-publish" className="h-11 rounded-full bg-[#EDE0CC] text-[#081F5C] font-semibold hover:bg-[#E5D3B8]">Preview & Publish</Button>
        </div>
      )}
    </div>
  );
}

function AdminsAdmin({ admin }) {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role: "admin" });
  const load = () => api.get("/admin/admins").then(({ data }) => setAdmins(data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line
  const add = async () => {
    try { await api.post("/admin/admins", form); toast.success("Admin added"); setForm({ name: "", email: "", role: "admin" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3">
      {admins.map((a) => (
        <div key={a.id} className="rounded-2xl border border-[#EDE0CC]/20 bg-[#0D2875] p-4 flex items-center justify-between" data-testid={`admin-user-${a.email}`}>
          <div><div className="font-semibold">{a.name}</div><div className="text-xs text-[#EDE0CC]/60">{a.email}</div></div>
          <span className="text-xs px-2 py-1 rounded-full bg-[#EDE0CC]/10">{a.role === "super_admin" ? "Super Admin" : "Admin"}</span>
        </div>
      ))}
      {admin.role === "super_admin" && (
        <div className="rounded-2xl border border-[#EDE0CC]/20 bg-[#0D2875] p-4 space-y-2">
          <div className="text-sm font-semibold">Add admin</div>
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={ai} />
          <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-add-email" className={ai} />
          <Button onClick={add} data-testid="admin-add-btn" className="h-10 rounded-full bg-[#EDE0CC] text-[#081F5C] font-semibold hover:bg-[#E5D3B8]">Add</Button>
        </div>
      )}
    </div>
  );
}

function AuditAdmin() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit").then(({ data }) => setLogs(data)).catch(() => {}); }, []);
  return (
    <div className="space-y-2" data-testid="admin-audit">
      {logs.length === 0 && <p className="text-sm text-[#EDE0CC]/60">No audit entries yet.</p>}
      {logs.map((l) => (
        <div key={l.id} className="rounded-xl border border-[#EDE0CC]/15 bg-[#0D2875] p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-semibold">{l.action}</span>
            <span className={`text-xs flex items-center gap-1 ${l.success ? "text-[#77BA99]" : "text-[#E56B6B]"}`}><Clock size={12} /> {new Date(l.timestamp).toLocaleString()}</span>
          </div>
          <div className="text-xs text-[#EDE0CC]/60 mt-0.5">{l.admin_email} · {l.record} {l.version ? `· v${l.version}` : ""}</div>
        </div>
      ))}
    </div>
  );
}

const ai = "h-11 rounded-xl bg-[#051540] border-[#EDE0CC]/30 text-[#EDE0CC]";
function Labeled({ label, children }) {
  return <div className="mb-2"><div className="text-xs text-[#EDE0CC]/60 mb-1">{label}</div>{children}</div>;
}
function StatusChip({ status }) {
  const map = { published: "bg-[#77BA99]/20 text-[#77BA99]", draft: "bg-[#EDE0CC]/15 text-[#EDE0CC]/80", archived: "bg-[#E56B6B]/15 text-[#E56B6B]" };
  return <span className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-full ${map[status] || ""}`}>{status}</span>;
}
function MiniBtn({ onClick, label, testid, icon: Icon, primary }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`flex items-center gap-1 h-9 px-3 rounded-full text-xs active:scale-95 transition-transform ${primary ? "bg-[#EDE0CC] text-[#081F5C] font-semibold" : "border border-[#EDE0CC]/30 text-[#EDE0CC]"}`}>
      <Icon size={14} /> {label}
    </button>
  );
}
